import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Column} from "primereact/column";
import {confirmDialog, ConfirmDialog} from "primereact/confirmdialog";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import PrimeDatePicker from "../../system/components/prime-date-picker";
import {
    ADMIN_PERMISSIONS,
    hasAdminPermission,
    hasPlatformServicesAccess,
} from "../../system/admin-access";
import {
    createServiceApiKey,
    createServiceClient,
    createTenantBinding,
    fetchCrmRegistry,
    fetchCrmWorkspaces,
    fetchPlatforms,
    fetchServiceApiKeys,
    fetchServiceClients,
    fetchTenantBindings,
    revokeServiceApiKey,
    rotateServiceApiKey,
    updateServiceClient,
    updateTenantBinding,
} from "../../system/platform-services-api";
import {useTranslate} from "../../system/useTranslate";
import {
    DataState,
    FormDialog,
    FormField,
    RTL_DATATABLE_PROPS,
    RTL_DROPDOWN_PROPS,
    SalesMultiSelect,
    SalesSectionHeader,
    SwitchField,
    TableCard,
} from "../sales/components/common";

const CLIENT_TYPES = [
    {value: "first_party", labelKey: "platform-services-client-type-first-party"},
    {value: "external", labelKey: "platform-services-client-type-external"},
];
const STATUSES = ["active", "disabled", "revoked"];

function requestError(error) {
    return error?.response?.data?.error?.message || error?.message || "platform-services-request-failed";
}

function selectedScopeKeys(value) {
    return (value || []).map((item) => String(
        typeof item === "string" ? item : item?._salesKey || item?.documentId || item?.value || ""
    )).filter(Boolean);
}

function statusSeverity(status) {
    if (status === "active") return "success";
    if (status === "disabled") return "warning";
    if (status === "revoked") return "danger";
    return "secondary";
}

const statusLabelKey = (status) => `platform-services-status-${status}`;

function PlatformServices() {
    const translate = useTranslate();
    const user = useSelector((state) => state.auth.user);
    const canManage = hasAdminPermission(user, ADMIN_PERMISSIONS.PLATFORM_SERVICES_MANAGE);
    const [platforms, setPlatforms] = useState([]);
    const [registry, setRegistry] = useState({modules: [], globalScopes: []});
    const [workspaces, setWorkspaces] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [apiKeys, setApiKeys] = useState([]);
    const [bindings, setBindings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [clientDialog, setClientDialog] = useState(false);
    const [keyDialog, setKeyDialog] = useState(false);
    const [keyToRotate, setKeyToRotate] = useState(null);
    const [bindingDialog, setBindingDialog] = useState(false);
    const [secret, setSecret] = useState(null);
    const [clientForm, setClientForm] = useState({name: "", clientId: "", clientType: "external", status: "active", allowedScopes: [], allowTenantProvisioning: false});
    const [keyForm, setKeyForm] = useState({name: "", scopes: [], expiresAt: null});
    const [bindingForm, setBindingForm] = useState({externalTenantId: "", externalTenantName: "", workspaceDocumentId: ""});

    const scopeOptions = useMemo(() => [
        ...(registry.modules || []).filter((module) => module.enabled && module.implemented)
            .flatMap((module) => (module.scopes || []).map((scope) => ({
                value: scope.key,
                title: translate(scope.titleKey),
            }))),
        ...(registry.globalScopes || []).map((scope) => ({value: scope.key, title: translate(scope.titleKey)})),
    ], [registry, translate]);

    const allowedKeyScopeOptions = useMemo(() => {
        const allowed = new Set(selectedClient?.allowedScopes || []);
        return scopeOptions.filter((option) => allowed.has(option.value));
    }, [scopeOptions, selectedClient]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const nextPlatforms = await fetchPlatforms();
            setPlatforms(nextPlatforms);
            const crm = nextPlatforms.find((platform) => platform.key === "crm");
            if (!crm?.available) {
                setRegistry({modules: [], globalScopes: []});
                setClients([]);
                setWorkspaces([]);
                setSelectedClient(null);
                return;
            }
            const [nextRegistry, nextClients, nextWorkspaces] = await Promise.all([
                fetchCrmRegistry(),
                fetchServiceClients(),
                fetchCrmWorkspaces(),
            ]);
            setRegistry(nextRegistry);
            setClients(nextClients);
            setWorkspaces(nextWorkspaces);
            setSelectedClient((current) => nextClients.find((client) => client.documentId === current?.documentId) || nextClients[0] || null);
        } catch (loadError) {
            setError(requestError(loadError));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDetails = useCallback(async (client) => {
        if (!client?.documentId) {
            setApiKeys([]);
            setBindings([]);
            return;
        }
        setDetailsLoading(true);
        setError(null);
        try {
            const [nextKeys, nextBindings] = await Promise.all([
                fetchServiceApiKeys(client.documentId),
                fetchTenantBindings(client.documentId),
            ]);
            setApiKeys(nextKeys);
            setBindings(nextBindings);
        } catch (loadError) {
            setError(requestError(loadError));
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasPlatformServicesAccess(user)) load();
    }, [load, user]);

    useEffect(() => {
        loadDetails(selectedClient);
    }, [loadDetails, selectedClient]);

    const openCreateClient = () => {
        setClientForm({name: "", clientId: "", clientType: "external", status: "active", allowedScopes: [], allowTenantProvisioning: false});
        setDialogError(null);
        setClientDialog(true);
    };

    const openEditClient = () => {
        if (!selectedClient) return;
        setClientForm({
            name: selectedClient.name || "",
            clientId: selectedClient.clientId || "",
            clientType: selectedClient.clientType || "external",
            status: selectedClient.status || "active",
            allowedScopes: selectedClient.allowedScopes || [],
            allowTenantProvisioning: selectedClient.allowTenantProvisioning === true,
        });
        setDialogError(null);
        setClientDialog(true);
    };

    const saveClient = async (event) => {
        event.preventDefault();
        setSaving(true);
        setDialogError(null);
        try {
            const editing = Boolean(selectedClient && clientForm.clientId === selectedClient.clientId);
            const payload = {
                name: clientForm.name,
                allowedScopes: selectedScopeKeys(clientForm.allowedScopes),
                allowTenantProvisioning: clientForm.allowTenantProvisioning,
                ...(editing ? {status: clientForm.status} : {clientId: clientForm.clientId || undefined, clientType: clientForm.clientType}),
            };
            const saved = editing
                ? await updateServiceClient(selectedClient.documentId, payload)
                : await createServiceClient(payload);
            setClientDialog(false);
            await load();
            setSelectedClient(saved);
        } catch (saveError) {
            setDialogError(requestError(saveError));
        } finally {
            setSaving(false);
        }
    };

    const openCreateKey = () => {
        setKeyToRotate(null);
        setKeyForm({name: "", scopes: [], expiresAt: null});
        setDialogError(null);
        setKeyDialog(true);
    };

    const openRotateKey = (row) => {
        setKeyToRotate(row);
        setKeyForm({
            name: row.name || "",
            scopes: selectedClient?.allowedScopes || row.scopes || [],
            expiresAt: row.expiresAt ? Math.floor(new Date(row.expiresAt).getTime() / 1000) : null,
        });
        setDialogError(null);
        setKeyDialog(true);
    };

    const closeKeyDialog = () => {
        setKeyDialog(false);
        setKeyToRotate(null);
    };

    const saveKey = async (event) => {
        event.preventDefault();
        setSaving(true);
        setDialogError(null);
        try {
            const payload = {
                name: keyForm.name,
                scopes: selectedScopeKeys(keyForm.scopes),
                expiresAt: keyForm.expiresAt ? new Date(keyForm.expiresAt * 1000).toISOString() : null,
            };
            const created = keyToRotate
                ? await rotateServiceApiKey(keyToRotate.documentId, payload)
                : await createServiceApiKey(selectedClient.documentId, payload);
            closeKeyDialog();
            setSecret(created.apiKey);
            await loadDetails(selectedClient);
        } catch (saveError) {
            setDialogError(requestError(saveError));
        } finally {
            setSaving(false);
        }
    };

    const revokeKey = (row) => confirmDialog({
        header: translate("platform-services-key-revoke-title"),
        message: translate("platform-services-key-revoke-confirm"),
        acceptLabel: translate("confirm"),
        rejectLabel: translate("cancel"),
        className: "rtl text-xs",
        acceptClassName: "p-button-danger text-xs",
        rejectClassName: "text-xs p-button-text",
        accept: async () => {
            try {
                await revokeServiceApiKey(row.documentId);
                await loadDetails(selectedClient);
            } catch (actionError) {
                setError(requestError(actionError));
            }
        },
    });

    const saveBinding = async (event) => {
        event.preventDefault();
        setSaving(true);
        setDialogError(null);
        try {
            await createTenantBinding(selectedClient.documentId, bindingForm);
            setBindingDialog(false);
            await loadDetails(selectedClient);
        } catch (saveError) {
            setDialogError(requestError(saveError));
        } finally {
            setSaving(false);
        }
    };

    const toggleBinding = async (row) => {
        try {
            await updateTenantBinding(row.documentId, {status: row.status === "active" ? "disabled" : "active"});
            await loadDetails(selectedClient);
        } catch (actionError) {
            setError(requestError(actionError));
        }
    };

    if (!hasPlatformServicesAccess(user)) {
        return <AdminAccessDenied titleKey="platform-services-access-denied-title" descriptionKey="platform-services-access-denied-description"/>;
    }

    const crmPlatform = platforms.find((platform) => platform.key === "crm");

    return (
        <div className="w-full h-full overflow-auto flex flex-column gap-3 p-3 rtl text-xs">
            <ConfirmDialog/>
            <PageHeaderButton
                title="platform-services-title"
                description="platform-services-description"
                titleClassName="text-2xl line-height-2"
                end={(
                    <Button
                        icon="pi pi-refresh"
                        label={translate("refresh")}
                        outlined
                        className="p-button-sm text-xs gap-2"
                        loading={loading}
                        onClick={load}
                    />
                )}
            />

            {error && <Message severity="error" text={translate(error)} className="text-xs" pt={{text: {className: "text-xs"}}}/>}

            {!loading && !error && !crmPlatform?.configured && <Message severity="info" text={translate("platform-services-crm-not-configured")} className="text-xs" pt={{text: {className: "text-xs"}}}/>}
            {!loading && !error && crmPlatform?.configured && !crmPlatform?.available && <Message severity="warn" text={translate("platform-services-crm-invalid")} className="text-xs" pt={{text: {className: "text-xs"}}}/>}

            {crmPlatform?.available && <>
                <Card className="border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                    <SalesSectionHeader
                        title={translate("platform-services-modules-title")}
                        description={translate("platform-services-modules-description")}
                    />
                    <div className="grid mt-2 mb-0">
                        {(registry.modules || []).map((module) => (
                            <div className="col-12 md:col-4" key={module.key}>
                                <div className="surface-50 border-1 border-200 border-round p-3 h-full flex flex-column gap-2">
                                    <div className="flex align-items-center justify-content-between gap-2">
                                        <span className="font-bold text-sm text-900">{translate(module.titleKey)}</span>
                                        <Tag className="text-xs" severity={module.enabled && module.implemented ? "success" : "secondary"} value={translate(module.enabled && module.implemented ? "platform-services-status-active" : "platform-services-coming-soon")}/>
                                    </div>
                                    <span className="text-xs text-600 line-height-3">{translate(module.descriptionKey)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <div className="grid m-0 align-items-stretch">
                    <div className="col-12 lg:col-4 flex">
                        <TableCard className="w-full h-full">
                            <div className="p-3 border-bottom-1 border-200">
                                <SalesSectionHeader
                                    title={translate("platform-services-clients-title")}
                                    description={translate("platform-services-clients-description")}
                                    actions={canManage && (
                                        <Button
                                            icon="pi pi-plus"
                                            label={translate("platform-services-client-add")}
                                            className="p-button-sm text-xs gap-2 white-space-nowrap flex-shrink-0"
                                            onClick={openCreateClient}
                                        />
                                    )}
                                />
                            </div>
                            <DataState loading={loading} empty={!clients.length} error={null}>
                                <DataTable
                                    {...RTL_DATATABLE_PROPS}
                                    value={clients}
                                    size="small"
                                    dataKey="documentId"
                                    selectionMode="single"
                                    selection={selectedClient}
                                    onSelectionChange={(event) => setSelectedClient(event.value)}
                                    emptyMessage={translate("datatable-empty-message")}
                                >
                                    <Column className="text-right" field="name" header={translate("name")} bodyClassName="text-right" headerClassName="text-right"/>
                                    <Column className="text-right" header={translate("status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.status)} value={translate(statusLabelKey(row.status))}/>} bodyClassName="text-right" headerClassName="text-right"/>
                                </DataTable>
                            </DataState>
                        </TableCard>
                    </div>

                    <div className="col-12 lg:col-8 flex">
                        {!selectedClient ? <Message severity="info" text={translate("platform-services-select-client")} className="w-full text-xs" pt={{text: {className: "text-xs"}}}/> : (
                            <TableCard className="w-full h-full">
                                <div className="p-3 border-bottom-1 border-200">
                                    <SalesSectionHeader
                                        title={selectedClient.name}
                                        description={`${translate("platform-services-client-id")}: ${selectedClient.clientId}`}
                                        actions={canManage && (
                                            <Button
                                                icon="pi pi-pencil"
                                                label={translate("edit")}
                                                outlined
                                                className="p-button-sm text-xs gap-2"
                                                onClick={openEditClient}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="surface-50 p-3 border-bottom-1 border-200 flex flex-column gap-2">
                                    <span className="text-xs font-bold text-700">{translate("platform-services-allowed-scopes")}</span>
                                    <div className="flex flex-wrap gap-2">
                                        {(selectedClient.allowedScopes || []).map((scope) => <Tag className="text-xs" key={scope} value={translate(scopeOptions.find((item) => item.value === scope)?.title || scope)}/>)}
                                    </div>
                                </div>
                                <TabView className="rtl text-xs" pt={{nav: {className: "px-3"}, panelContainer: {className: "p-0"}}}>
                                    <TabPanel header={translate("platform-services-api-keys-title")}>
                                        <div className="flex justify-content-end p-3 border-bottom-1 border-200">
                                            {canManage && <Button icon="pi pi-plus" label={translate("platform-services-key-add")} className="p-button-sm text-xs gap-2" onClick={openCreateKey}/>} 
                                        </div>
                                        <DataState loading={detailsLoading} empty={!apiKeys.length} error={null}>
                                            <DataTable {...RTL_DATATABLE_PROPS} value={apiKeys} dataKey="documentId" size="small" emptyMessage={translate("datatable-empty-message")}>
                                                <Column className="text-right" field="name" header={translate("name")} bodyClassName="text-right" headerClassName="text-right"/>
                                                <Column className="text-right" field="keyPrefix" header={translate("platform-services-key-prefix")} bodyClassName="text-right ltr" headerClassName="text-right"/>
                                                <Column className="text-right" header={translate("status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.status)} value={translate(statusLabelKey(row.status))}/>} bodyClassName="text-right" headerClassName="text-right"/>
                                                <Column className="text-right" header={translate("actions")} body={(row) => canManage && row.status === "active" ? <div className="flex flex-wrap gap-2">
                                                    <Button label={translate("platform-services-key-rotate")} text className="text-xs" onClick={() => openRotateKey(row)}/>
                                                    <Button label={translate("platform-services-key-revoke")} text severity="danger" className="text-xs" onClick={() => revokeKey(row)}/>
                                                </div> : null} bodyClassName="text-right" headerClassName="text-right"/>
                                            </DataTable>
                                        </DataState>
                                    </TabPanel>
                                    <TabPanel header={translate("platform-services-bindings-title")}>
                                        <div className="flex justify-content-end p-3 border-bottom-1 border-200">
                                            {canManage && <Button icon="pi pi-plus" label={translate("platform-services-binding-add")} className="p-button-sm text-xs gap-2" onClick={() => {
                                                setBindingForm({externalTenantId: "", externalTenantName: "", workspaceDocumentId: ""});
                                                setDialogError(null);
                                                setBindingDialog(true);
                                            }}/>} 
                                        </div>
                                        <DataState loading={detailsLoading} empty={!bindings.length} error={null}>
                                            <DataTable {...RTL_DATATABLE_PROPS} value={bindings} dataKey="documentId" size="small" emptyMessage={translate("datatable-empty-message")}>
                                                <Column className="text-right" field="externalTenantId" header={translate("platform-services-external-tenant-id")} bodyClassName="text-right ltr" headerClassName="text-right"/>
                                                <Column className="text-right" header={translate("platform-services-crm-workspace")} body={(row) => row.workspace?.name || "-"} bodyClassName="text-right" headerClassName="text-right"/>
                                                <Column className="text-right" header={translate("status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.status)} value={translate(statusLabelKey(row.status))}/>} bodyClassName="text-right" headerClassName="text-right"/>
                                                <Column className="text-right" header={translate("actions")} body={(row) => canManage && row.status !== "revoked" ? <Button label={translate(row.status === "active" ? "platform-services-disable" : "platform-services-activate")} text className="text-xs" onClick={() => toggleBinding(row)}/> : null} bodyClassName="text-right" headerClassName="text-right"/>
                                            </DataTable>
                                        </DataState>
                                    </TabPanel>
                                </TabView>
                            </TableCard>
                        )}
                    </div>
                </div>
            </>}

            <FormDialog visible={clientDialog} title={translate(selectedClient && clientForm.clientId === selectedClient.clientId ? "platform-services-client-edit" : "platform-services-client-add")} description={translate("platform-services-client-dialog-description")} saving={saving} error={dialogError ? translate(dialogError) : null} onHide={() => setClientDialog(false)} onSubmit={saveClient} submitDisabled={!clientForm.name || !selectedScopeKeys(clientForm.allowedScopes).length}>
                <FormField label={translate("name")} required>
                    <InputText value={clientForm.name} onChange={(event) => setClientForm((current) => ({...current, name: event.target.value}))} className="w-full text-xs"/>
                </FormField>
                <FormField label={translate("platform-services-client-id")}>
                    <InputText value={clientForm.clientId} disabled={Boolean(selectedClient && clientForm.clientId === selectedClient.clientId)} onChange={(event) => setClientForm((current) => ({...current, clientId: event.target.value}))} className="w-full text-xs ltr"/>
                </FormField>
                <FormField label={translate("platform-services-client-type")}>
                    <Dropdown {...RTL_DROPDOWN_PROPS} value={clientForm.clientType} disabled={Boolean(selectedClient && clientForm.clientId === selectedClient.clientId)} options={CLIENT_TYPES.map((item) => ({...item, label: translate(item.labelKey)}))} optionLabel="label" optionValue="value" onChange={(event) => setClientForm((current) => ({...current, clientType: event.value}))}/>
                </FormField>
                {selectedClient && clientForm.clientId === selectedClient.clientId && <FormField label={translate("status")}>
                    <Dropdown {...RTL_DROPDOWN_PROPS} value={clientForm.status} options={STATUSES.map((value) => ({value, label: translate(statusLabelKey(value))}))} optionLabel="label" optionValue="value" onChange={(event) => setClientForm((current) => ({...current, status: event.value}))}/>
                </FormField>}
                <FormField label={translate("platform-services-allowed-scopes")} required className="col-12">
                    <SalesMultiSelect value={clientForm.allowedScopes} options={scopeOptions} onChange={(event) => setClientForm((current) => ({...current, allowedScopes: event.value}))} placeholderKey="platform-services-select-scopes" searchPlaceholderKey="search"/>
                </FormField>
                <SwitchField className="col-12" label={translate("platform-services-auto-provision")} checked={clientForm.allowTenantProvisioning} onChange={(value) => setClientForm((current) => ({...current, allowTenantProvisioning: value}))}/>
            </FormDialog>

            <FormDialog visible={keyDialog} title={translate(keyToRotate ? "platform-services-key-rotate-title" : "platform-services-key-add")} description={translate(keyToRotate ? "platform-services-key-rotate-description" : "platform-services-key-dialog-description")} saving={saving} error={dialogError ? translate(dialogError) : null} onHide={closeKeyDialog} onSubmit={saveKey} submitDisabled={!keyForm.name || !selectedScopeKeys(keyForm.scopes).length}>
                <FormField label={translate("name")} required>
                    <InputText value={keyForm.name} onChange={(event) => setKeyForm((current) => ({...current, name: event.target.value}))} className="w-full text-xs"/>
                </FormField>
                <FormField label={translate("platform-services-key-expiration")}>
                    <PrimeDatePicker inputClassName="w-full text-xs" value={keyForm.expiresAt} onChange={(event) => setKeyForm((current) => ({...current, expiresAt: event.value}))} placeholder={translate("platform-services-key-expiration")}/>
                </FormField>
                <FormField label={translate("platform-services-key-scopes")} required className="col-12">
                    <SalesMultiSelect value={keyForm.scopes} options={allowedKeyScopeOptions} onChange={(event) => setKeyForm((current) => ({...current, scopes: event.value}))} placeholderKey="platform-services-select-scopes" searchPlaceholderKey="search"/>
                </FormField>
            </FormDialog>

            <FormDialog visible={bindingDialog} title={translate("platform-services-binding-add")} description={translate("platform-services-binding-dialog-description")} saving={saving} error={dialogError ? translate(dialogError) : null} onHide={() => setBindingDialog(false)} onSubmit={saveBinding} submitDisabled={!bindingForm.externalTenantId || !bindingForm.workspaceDocumentId}>
                <FormField label={translate("platform-services-external-tenant-id")} required>
                    <InputText value={bindingForm.externalTenantId} onChange={(event) => setBindingForm((current) => ({...current, externalTenantId: event.target.value}))} className="w-full text-xs ltr"/>
                </FormField>
                <FormField label={translate("platform-services-external-tenant-name")}>
                    <InputText value={bindingForm.externalTenantName} onChange={(event) => setBindingForm((current) => ({...current, externalTenantName: event.target.value}))} className="w-full text-xs"/>
                </FormField>
                <FormField label={translate("platform-services-crm-workspace")} required className="col-12">
                    <Dropdown {...RTL_DROPDOWN_PROPS} filter value={bindingForm.workspaceDocumentId} options={workspaces} optionLabel="name" optionValue="documentId" placeholder={translate("platform-services-select-crm-workspace")} onChange={(event) => setBindingForm((current) => ({...current, workspaceDocumentId: event.value}))}/>
                </FormField>
            </FormDialog>

            <Dialog visible={Boolean(secret)} modal draggable={false} closable={false} header={translate("platform-services-secret-title")} className="rtl w-11 md:w-8 lg:w-6 border-round-2xl" onHide={() => {}}>
                <div className="flex flex-column gap-3">
                    <Message severity="warn" text={translate("platform-services-secret-once")}/>
                    <InputText value={secret || ""} readOnly className="w-full ltr text-xs"/>
                    <div className="flex justify-content-end gap-2">
                        <Button label={translate("platform-services-copy")} outlined className="text-xs" onClick={() => navigator.clipboard?.writeText(secret || "")}/>
                        <Button label={translate("confirm")} className="text-xs" onClick={() => setSecret(null)}/>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default PlatformServices;
